import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Clock, FileText, ArrowRight, Phone, MessageCircle,
  Shield, AlertCircle, ChevronDown, ChevronUp, HelpCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { PAGE_CONTENT } from '@/data/pageContent';
import { useState } from 'react';

const SaudiVisaPage = () => {
  const { visaType } = useParams();
  const [openFaq, setOpenFaq] = useState(null);
  
  const pageKey = `saudi-arabia/${visaType}`;
  const pageData = PAGE_CONTENT[pageKey];

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Page Not Found</h1>
          <Button asChild>
            <Link to="/saudi-arabia">Back to Saudi Arabia</Link>
          </Button>
        </div>
      </div>
    );
  }

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div data-testid={`saudi-${visaType}-page`}>
      <PageHeader
        title={pageData.title}
        subtitle={pageData.subtitle}
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Saudi Arabia', path: '/saudi-arabia' },
          { label: pageData.title, path: `/saudi-arabia/${visaType}` }
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-3">
        <div className="container mx-auto px-6 text-center">
          <p className="text-sm md:text-base">
            <Shield className="w-4 h-4 inline mr-2" />
            <strong>Government Approved Agency</strong> – MEA RC: B-0762/DEL/PER/1000+/5/9148/2015
          </p>
        </div>
      </div>

      {/* Introduction Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-4">Overview</h2>
                <p className="text-gray-600 leading-relaxed mb-6">{pageData.content.intro}</p>
                
                {pageData.content.whatIsIt && (
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="font-bold text-zafco-blue mb-3">What is {pageData.title}?</h3>
                    <p className="text-gray-600 text-sm">{pageData.content.whatIsIt}</p>
                  </div>
                )}

                {pageData.content.whatIsWakala && (
                  <div className="bg-gray-50 rounded-xl p-6 mb-6">
                    <h3 className="font-bold text-zafco-blue mb-3">Understanding Wakala</h3>
                    <p className="text-gray-600 text-sm">{pageData.content.whatIsWakala}</p>
                  </div>
                )}

                {/* Key Features */}
                {pageData.content.keyFeatures && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-zafco-blue mb-4">Key Features</h3>
                    <div className="grid md:grid-cols-2 gap-3">
                      {pageData.content.keyFeatures.map((feature) => (
                        <div key={feature} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-600 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visa Details */}
                {pageData.content.visaDetails && (
                  <div className="bg-zafco-blue/5 rounded-xl p-6 mb-8">
                    <h3 className="font-bold text-zafco-blue mb-4">Visa Details</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {Object.entries(pageData.content.visaDetails).map(([key, value]) => (
                        <div key={key}>
                          <div className="text-xs text-gray-500 uppercase">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                          <div className="font-medium text-gray-800">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visa Types */}
                {pageData.content.visaTypes && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-zafco-blue mb-4">Visa Types</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {pageData.content.visaTypes.map((type) => (
                        <div key={type.type} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="font-bold text-zafco-blue">{type.type}</div>
                          <div className="text-sm text-gray-500 mt-1">
                            <span className="block">Validity: {type.validity}</span>
                            <span className="block">Stay: {type.stay}</span>
                            <span className="block text-zafco-teal">{type.ideal}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Visa Categories */}
                {pageData.content.visaCategories && (
                  <div className="mb-8">
                    <h3 className="text-xl font-bold text-zafco-blue mb-4">Visa Categories</h3>
                    <div className="space-y-3">
                      {pageData.content.visaCategories.map((cat) => (
                        <div key={cat.category} className="bg-white border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-800">{cat.category}</div>
                            <div className="text-sm text-gray-500">{cat.description}</div>
                          </div>
                          <div className="text-sm text-zafco-teal font-medium">{cat.processing}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Quick Contact */}
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="font-bold text-zafco-blue mb-4">Get Expert Help</h3>
                  <div className="space-y-3">
                    <Button asChild className="w-full bg-green-600 hover:bg-green-700">
                      <a href="https://wa.me/919958848998" target="_blank" rel="noopener noreferrer">
                        <MessageCircle className="w-4 h-4 mr-2" />
                        WhatsApp Now
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="w-full">
                      <a href="tel:+919958848998">
                        <Phone className="w-4 h-4 mr-2" />
                        Call Us
                      </a>
                    </Button>
                  </div>
                </div>

                {/* Processing Time */}
                {pageData.content.processingTime && (
                  <div className="bg-zafco-teal/10 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-zafco-teal" />
                      <span className="font-bold text-zafco-blue">Processing Time</span>
                    </div>
                    <p className="text-gray-600 text-sm">{pageData.content.processingTime}</p>
                  </div>
                )}

                {/* Related Services */}
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="font-bold text-zafco-blue mb-4">Related Services</h3>
                  <div className="space-y-2">
                    <Link to="/saudi-arabia/work-visa" className="block text-sm text-gray-600 hover:text-zafco-teal">
                      → Saudi Work Visa
                    </Link>
                    <Link to="/saudi-arabia/family-residence-visa" className="block text-sm text-gray-600 hover:text-zafco-teal">
                      → Family Residence Visa
                    </Link>
                    <Link to="/saudi-arabia/visa-stamping" className="block text-sm text-gray-600 hover:text-zafco-teal">
                      → Visa Stamping Services
                    </Link>
                    <Link to="/saudi-arabia/wakala" className="block text-sm text-gray-600 hover:text-zafco-teal">
                      → Wakala Services
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Section */}
      {pageData.content.eligibility && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-6">
              Eligibility Requirements
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pageData.content.eligibility.map((item, index) => (
                <motion.div
                  key={item}
                  className="bg-white rounded-lg p-4 flex items-start gap-3"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Documents Section */}
      {pageData.content.documents && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-6">
              <FileText className="w-6 h-6 inline mr-2" />
              Required Documents
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {pageData.content.documents.map((doc, index) => (
                <motion.div
                  key={doc}
                  className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="w-6 h-6 rounded-full bg-zafco-blue text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <span className="text-gray-600 text-sm">{doc}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Process Steps */}
      {pageData.content.processSteps && (
        <section className="py-12 bg-gradient-to-br from-zafco-blue/5 to-zafco-teal/5">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-8 text-center">
              Step-by-Step Process
            </h2>
            <div className="max-w-4xl mx-auto">
              {pageData.content.processSteps.map((step, index) => (
                <motion.div
                  key={step.title || step.step || `process-${index}`}
                  className="flex gap-6 mb-6"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-zafco-blue text-white flex items-center justify-center font-bold">
                      {step.step}
                    </div>
                    {index < pageData.content.processSteps.length - 1 && (
                      <div className="w-0.5 h-full bg-zafco-blue/20 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-start">
                      <h3 className="font-bold text-gray-800">{step.title}</h3>
                      <span className="text-xs bg-zafco-teal/10 text-zafco-teal px-2 py-1 rounded">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm mt-1">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Benefits/Advantages Section */}
      {(pageData.content.benefits || pageData.content.advantages) && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-6">
              {pageData.content.benefits ? 'Benefits' : 'Advantages'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(pageData.content.benefits || pageData.content.advantages).map((item) => (
                <div key={item} className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Tourist Attractions (for tourist visa) */}
      {pageData.content.touristAttractions && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-6">
              Top Destinations to Explore
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pageData.content.touristAttractions.map((attraction) => (
                <div key={attraction.name} className="bg-white rounded-xl p-5 shadow-sm">
                  <h3 className="font-bold text-zafco-blue mb-2">{attraction.name}</h3>
                  <p className="text-gray-600 text-sm">{attraction.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      {pageData.content.faqs && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-6">
            <h2 className="text-2xl font-bold text-zafco-blue font-montserrat mb-8 text-center">
              <HelpCircle className="w-6 h-6 inline mr-2" />
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {pageData.content.faqs.map((faq) => (
                <motion.div
                  key={faq.q}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <button
                    onClick={() => toggleFaq(faq.q)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between bg-white hover:bg-gray-50"
                  >
                    <span className="font-medium text-gray-800">{faq.q}</span>
                    {openFaq === faq.q ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  {openFaq === faq.q && (
                    <div className="px-6 py-4 bg-gray-50 border-t">
                      <p className="text-gray-600 text-sm">{faq.a}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-zafco-teal">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-3xl font-bold font-montserrat mb-4">
                Ready to Apply for {pageData.title}?
              </h2>
              <p className="text-white/80 mb-6">
                Our expert team will guide you through every step of the process. 
                Get personalized assistance and fast processing with Zafco.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Free Assessment</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Expert Guidance</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Fast Processing</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm">
                  <strong>Authorized Agency:</strong> MEA RC: B-0762/DEL/PER/1000+/5/9148/2015
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-xl font-bold text-zafco-blue mb-4">Start Your Application</h3>
              <ConsultationForm compact />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SaudiVisaPage;
